import { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import { api } from '../../lib/api';
import { useAuthStore } from '../../store/auth';
import { useEntitlements } from '../../hooks/useEntitlements';

interface Metrics {
    total_players: number;
    total_sessions: number;
    total_plays: number;
    total_rewards_awarded: number;
    conversion_rate: number;
    unique_players: number;
    sessions_by_status: Record<string, number>;
    rewards_by_type: Record<string, number>;
    plays_by_day: Record<string, number>;
}

export default function Reports() {
    const { hasFeature, entitlements } = useEntitlements();
    const canExport = hasFeature('can_export');
    const [metrics, setMetrics] = useState<Metrics | null>(null);
    const [loading, setLoading] = useState(true);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const { user } = useAuthStore();
    const tenantId = localStorage.getItem('impersonated_tenant_id') || user?.tenant_id || '';

    useEffect(() => {
        const fetchMetrics = async () => {
            setLoading(true);
            try {
                const data = await api.getReportMetrics(tenantId, startDate || undefined, endDate || undefined);
                setMetrics(data);
            } catch (err) {
                console.error('Failed to fetch metrics:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchMetrics();
    }, [tenantId, startDate, endDate]);

    const handleExport = async () => {
        window.open(`/api/reports/metrics/export?tenantId=${tenantId}${startDate ? `&startDate=${startDate}` : ''}${endDate ? `&endDate=${endDate}` : ''}`);
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64"><div className="text-gray-500">Đang tải...</div></div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Báo cáo</h1>
                    <p className="text-gray-500 mt-1">Phân tích và chỉ số hiệu suất</p>
                </div>
                {canExport ? (
                    <button onClick={handleExport} className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700">
                        Xuất CSV
                    </button>
                ) : (
                    <button
                        onClick={() => alert(`Tính năng Xuất CSV báo cáo yêu cầu nâng cấp lên gói cước hạng Sự kiện trở lên. Gói hiện tại của bạn là: ${entitlements?.plan_label || 'Dùng thử'}`)}
                        className="bg-gray-200 border border-gray-300 text-gray-400 px-4 py-2 rounded-lg cursor-not-allowed hover:bg-gray-100 flex items-center gap-1"
                        title="Yêu cầu nâng cấp gói cước để Xuất CSV"
                    >
                        🔒 Xuất CSV (Cần nâng cấp)
                    </button>
                )}
            </div>

            {/* Date Filter */}
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 mb-6">
                <div className="flex gap-4">
                    <div>
                        <label className="block text-sm text-gray-500 mb-1">Ngày bắt đầu</label>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-lg"
                        />
                    </div>
                    <div>
                        <label className="block text-sm text-gray-500 mb-1">Ngày kết thúc</label>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-lg"
                        />
                    </div>
                </div>
            </div>

            {/* Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <p className="text-sm text-gray-500">Tổng người chơi</p>
                    <p className="text-3xl font-bold text-blue-600 mt-2">{metrics?.total_players || 0}</p>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <p className="text-sm text-gray-500">Tổng phiên chơi</p>
                    <p className="text-3xl font-bold text-green-600 mt-2">{metrics?.total_sessions || 0}</p>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <p className="text-sm text-gray-500">Tổng lượt chơi</p>
                    <p className="text-3xl font-bold text-purple-600 mt-2">{metrics?.total_plays || 0}</p>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <p className="text-sm text-gray-500">Phần thưởng đã trao</p>
                    <p className="text-3xl font-bold text-orange-600 mt-2">{metrics?.total_rewards_awarded || 0}</p>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <p className="text-sm text-gray-500">Tỷ lệ chuyển đổi</p>
                    <p className="text-3xl font-bold text-teal-600 mt-2">{metrics?.conversion_rate || 0}%</p>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <p className="text-sm text-gray-500">Người chơi duy nhất</p>
                    <p className="text-3xl font-bold text-indigo-600 mt-2">{metrics?.unique_players || 0}</p>
                </div>
            </div>

            {/* Sessions by Status */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Phiên chơi theo trạng thái</h2>
                <div className="space-y-3">
                    {Object.entries(metrics?.sessions_by_status || {}).map(([status, count]) => (
                        <div key={status} className="flex items-center gap-4">
                            <span className="text-sm text-gray-700 w-32 capitalize">{status}</span>
                            <div className="flex-1 bg-gray-100 rounded-full h-4">
                                <div
                                    className="bg-blue-600 h-4 rounded-full"
                                    style={{ width: `${metrics?.total_sessions ? (count / metrics.total_sessions) * 100 : 0}%` }}
                                />
                            </div>
                            <span className="text-sm text-gray-500 w-16 text-right">{count}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Rewards by Type */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Phần thưởng theo loại</h2>
                <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-green-50 rounded-lg">
                        <p className="text-sm text-green-600">Trúng</p>
                        <p className="text-2xl font-bold text-green-700">{metrics?.rewards_by_type?.won || 0}</p>
                    </div>
                    <div className="p-4 bg-red-50 rounded-lg">
                        <p className="text-sm text-red-600">Trượt</p>
                        <p className="text-2xl font-bold text-red-700">{metrics?.rewards_by_type?.lost || 0}</p>
                    </div>
                </div>
            </div>
        </Layout>
    );
}